import React, { useEffect, useMemo, useState } from 'react';

import { Check, ChevronDown, TriangleAlert } from 'lucide-react';

import { cn } from '../lib/utils';
import { compareLabelNames } from '../utils/labelSort';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './DropdownMenu';
import { LabelChip } from './LabelChip';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';

/**
 * The label cell of a row in the category contents table, plus the menu it opens.
 *
 * One component covers every row, because the frames show ONE menu with a
 * row-dependent header and footer. The row arrives CLASSIFIED — the server's
 * assignment stage, its rule matches and its applied label — so nothing here
 * re-runs a rule:
 *
 * | row                                  | trigger                             | frame       |
 * | ------------------------------------ | ----------------------------------- | ----------- |
 * | `labeled` (one rule applied itself)  | chip + chevron                      | 12124-22529 |
 * | `manual` (a person set it)           | locked chip + chevron               | 12133-34927 |
 * | `needsDecision` (2+ rules, no pick)  | candidate chips + warning + chevron | 12135-44693 |
 * | `unlabeled`                          | "+ Add label" + chevron, centred    | 12463-42280 |
 *
 * Two menu details are easy to get backwards, and the frames settle both:
 *
 * - The right-aligned "Rule match" annotation appears when two or more rules
 *   reach the content (12132-32578, 12133-34927), or on a hand-set row, where the
 *   check has moved off the match. A rule-labelled row (12124-22529) annotates nothing.
 * - "Remove manual assignment" appears ONLY when a person set the row (12133-34927), never on
 *   a row the rules labelled by themselves (12124-22529).
 *
 * "Remove manual assignment" reaches
 * `removeContentLabelManualAssignments`, which drops the person's assignment and
 * hands the content back to its rules.
 */

/**
 * How many candidate chips the needs-decision cell draws before it folds the rest
 * into `+ N`. Two, matching `MAX_VISIBLE_RULE_CHIPS` in `LabelRow` — the two
 * columns sit side by side and a different cap in each would read as a bug.
 */
const MAX_VISIBLE_CANDIDATE_CHIPS = 2;

/**
 * The props are STRUCTURAL, not the Settings domain types.
 *
 * Two surfaces draw this picker now — the Settings stage tables and the
 * portfolio Content tab's per-category columns — and `components-ui` carries a
 * written prohibition on importing from `routes/`. The Settings
 * `LabelCategory` / `LabelDefinition` / `LabelAssignmentRow` are all still
 * assignable to the shapes below, which is why every existing call site
 * compiled untouched; the Content tab's `ContentLabelCellRow` is assignable to
 * the same ones.
 *
 * Only the fields the picker actually READS are declared, so neither caller has
 * to invent a `parentTitle` or a `labelCategoryId` it does not have.
 */

/** One label of the category, as the menu lists it and the cell chips it. */
export type RowLabelPickerLabel = {
  labelId: string;
  name: string;
};

export type RowLabelPickerCategory = {
  name: string;
  /** The category hue every chip in the column is tinted from. */
  color: string;
  labels: RowLabelPickerLabel[];
};

/** The four assignment stages the server computes, named as `ContentsByAssignmentStage` names them. */
export type RowLabelPickerStage = 'labeled' | 'needsDecision' | 'manual' | 'unlabeled';

export type RowLabelPickerRow = {
  /** The title is what every accessible name here is built from. */
  content: { title: string };
  stage: RowLabelPickerStage;
  /** Labels whose SAVED rule reaches this content, in the category's label order. */
  matchIds?: string[] | null;
  appliedLabelId: string | null;
  setByHand: boolean;
};

export type RowLabelPickerProps = {
  category: RowLabelPickerCategory;
  /** The row as the server classified it. */
  row: RowLabelPickerRow;
  /** "Country label" — from `labelColumnLabel`, so the cell and its header agree. */
  columnNoun: string;
  /**
   * A write for this category is in flight: the menu will not open, so no second
   * one starts. The trigger stays FOCUSABLE — see the note on the menu below.
   */
  busy?: boolean;
  /**
   * Every row on screen carries this row's stage — a Settings stage table, as against the Content
   * tab's mixed column. The cell then stops repeating the glyph the view has already shown once.
   */
  stageScoped?: boolean;
  onAssign: (labelId: string) => void;
  onReset: () => void;
};

/**
 * The white box both triggers wear on hover and while open (12463-42259, 12473-46060). The border
 * is there at rest too, transparent, so the box appearing moves no pixel. Keyed to `aria-expanded`,
 * since the tooltip wrapper owns `data-state`.
 */
const HOVER_BOX_CLASS = cn(
  'rounded-[4px] border border-transparent',
  'transition-[color,background-color,border-color] duration-150 ease-out motion-reduce:transition-none',
  'group-focus-visible:ring-2 group-focus-visible:ring-green-400 group-focus-visible:ring-offset-1',
  'group-hover:border-neutral-100 group-hover:bg-white',
  'group-aria-expanded:border-neutral-100 group-aria-expanded:bg-white',
);

/** 16px at a 1.5 stroke reproduces the frame's chevron ink. Decorative: the button carries the name. */
function Chevron(): JSX.Element {
  return (
    <ChevronDown
      aria-hidden="true"
      strokeWidth={1.5}
      className="size-4 shrink-0 text-neutral-300 transition-colors duration-150 ease-out group-hover:text-neutral-600 group-aria-expanded:text-neutral-600 motion-reduce:transition-none"
    />
  );
}

export function RowLabelPicker({
  category,
  row,
  columnNoun,
  busy = false,
  stageScoped = false,
  onAssign,
  onReset,
}: RowLabelPickerProps): JSX.Element {
  const labels = category.labels ?? [];
  const menuLabels = useMemo(() => [...labels].sort(compareLabelNames), [labels]);
  const { content, stage, appliedLabelId: applied, setByHand } = row;

  const [open, setOpen] = useState(false);

  // `busy` is category-wide, so it can arrive while this menu is open (the owner
  // flips it from anywhere in the card). Shut the menu — for good, not just while
  // busy, so it does not spring back open over a table the reader has moved on
  // from. The same rule the bulk bar's picker follows.
  useEffect(() => {
    if (busy) setOpen(false);
  }, [busy]);

  /**
   * The rule matches, in CATEGORY order and narrowed to labels the category
   * still holds: a match on a label deleted a moment ago has no name to draw,
   * and counting it would announce "3 options" over two chips.
   */
  const matchIds = new Set(row.matchIds ?? []);
  const eligible = labels.filter((label) => matchIds.has(label.labelId)).map((label) => label.labelId);

  /**
   * A view scoped to one stage names it above the table, so a triangle on every row is repetition.
   * It returns when no candidate chip survives, which would otherwise leave the cell blank.
   */
  const showsClashGlyph = !stageScoped || eligible.length === 0;

  const appliedName = labels.find((label) => label.labelId === applied)?.name ?? null;
  const hasChips = stage === 'needsDecision' || appliedName !== null;
  const optionsText = `${eligible.length} options`;

  // One match needs no annotation while the check sits on it; a hand-set row moves the check away.
  const annotateMatches = eligible.length >= 2 || (setByHand && eligible.length > 0);
  /**
   * A category with no labels yet has nothing for this menu to offer. The stage
   * tables now open on such a category — its coverage is real, every content is
   * unlabelled — so the trigger has to say why it cannot act instead of opening
   * an EMPTY menu, which reads as broken rather than as "make a label first".
   *
   * `aria-disabled` rather than `disabled`, for the reasons the busy guard below
   * spells out: a native disabled button still takes Radix's pointerdown, and it
   * cannot hold focus.
   */
  const noLabelsYet = category.labels.length === 0;

  const triggerName = noLabelsYet
    ? `No labels to assign yet in ${category.name}`
    : stage === 'needsDecision'
      ? `Resolve ${columnNoun} for ${content.title}, ${optionsText}`
      : appliedName
        ? `Change ${columnNoun} for ${content.title}, currently ${appliedName}${setByHand ? ', set by hand' : ''}`
        : `Add ${columnNoun} to ${content.title}`;

  const trigger = (
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        aria-label={triggerName}
        aria-disabled={busy || noLabelsYet || undefined}
        className={cn(
          'group inline-flex w-full items-center justify-center rounded-[3px]',
          // The box overhangs this button, so its white would cover a ring here; the box wears it.
          'focus-visible:outline-none',
          'aria-disabled:cursor-not-allowed aria-disabled:opacity-60',
        )}
      >
        {hasChips ? (
          /* 12473-46060: chips with the chevron right after them, in a 34px box that overhangs the
             cell's padding by 4px a side, so the row stays 46px. 3px + the border = the frame's 4px. */
          <span
            className={cn(
              HOVER_BOX_CLASS,
              '-my-1 inline-flex h-[34px] min-w-0 max-w-full items-center gap-0.5 px-[3px]',
            )}
          >
            {stage === 'needsDecision' ? (
              /* 12135:44693 draws the CANDIDATES themselves — `IT` `DE` — followed
                 by a bare warning triangle, not a count. Naming the labels is the
                 whole point of the cell: "2 options" makes you open a menu to learn
                 what a chip pair says at a glance. The count survives as the
                 trigger's accessible name, so nothing is lost to a screen reader.

                 `orange-aa` (#8F5214, 6.20:1) rather than the frame's `orange-400`
                 (#F6820D, 2.59:1 on white). The triangle is what separates "these
                 two labels both match" from "both are applied", so it is a graphic
                 REQUIRED to understand the cell — 1.4.11's 3:1 applies and the
                 frame's amber misses it. Same substitution the pill it replaced
                 already carried. */
              <span className="flex min-w-0 items-center gap-1.5">
                {eligible.slice(0, MAX_VISIBLE_CANDIDATE_CHIPS).map((labelId) => {
                  const name = labels.find((label) => label.labelId === labelId)?.name ?? '';
                  return (
                    <LabelChip
                      key={labelId}
                      categoryColor={category.color}
                      className="border border-dashed border-[var(--cfg)] bg-transparent px-[9px] py-[3px]"
                    >
                      <span className="min-w-0 truncate">{name}</span>
                    </LabelChip>
                  );
                })}
                {/* The overflow, exactly as 12483:50481 and 12483:53535 draw it —
                    `ITALY` `DENMARK` `+2`. Without the cap a content four rules
                    reach rendered four chips into a 200px column and pushed the
                    triangle, which is the thing that says "undecided", out of the
                    cell. Display only: the trigger's name still counts every
                    option, and the menu still lists them all. */}
                {eligible.length > MAX_VISIBLE_CANDIDATE_CHIPS ? (
                  <span
                    aria-hidden="true"
                    className="shrink-0 rounded-[3px] bg-neutral-100 px-1.5 py-1 text-[13px] font-semibold leading-[18px] text-neutral-600"
                  >
                    +{eligible.length - MAX_VISIBLE_CANDIDATE_CHIPS}
                  </span>
                ) : null}
                {showsClashGlyph ? (
                  <TriangleAlert aria-hidden="true" className="size-4 shrink-0 text-orange-aa" />
                ) : null}
              </span>
            ) : (
              /* A person set it: the lock sits inside the chip, as in the label form. Decorative —
                 the trigger's name already ends ", set by hand". */
              <LabelChip categoryColor={category.color} locked={setByHand}>
                <span className="min-w-0 truncate">{appliedName}</span>
              </LabelChip>
            )}
            {/* The frame's 28x26 chevron button, 2px after the chips. */}
            <span className="flex h-[26px] w-7 shrink-0 items-center justify-center">
              <Chevron />
            </span>
          </span>
        ) : (
          /* 12463-42280 at rest: bare text and chevron. 12463-42259 open: the same white box. */
          <span
            className={cn(
              HOVER_BOX_CLASS,
              // `-mt-1 -mb-[5px]`: the box overhangs the cell's padding, 6px from the top, so the row stays 46px.
              'mx-auto -mb-[5px] -mt-1 inline-flex h-[34px] items-center gap-2 pl-3 pr-2.5',
              'text-[13px] font-medium leading-[19px] tracking-[0.26px] text-neutral-300',
              'group-hover:text-neutral-500 group-aria-expanded:text-neutral-500',
            )}
          >
            + Add label
            {/* gap-2/pr-2.5 keep the chevron's centre where the frame puts it. */}
            <Chevron />
          </span>
        )}
      </button>
    </DropdownMenuTrigger>
  );

  return (
    /* CONTROLLED, and that is the busy guard — not the native `disabled`
       attribute, which failed twice over:

       - It never held a mouse user back. Radix opens this menu on POINTERDOWN,
         checking only its own `disabled` prop, and Chromium and Firefox still
         deliver pointerdown to a disabled button (React suppresses only the
         MOUSE events there). A press opened the menu and a pick started a second
         write over the first.
       - It threw focus away. The owner flips `busy` in the same batch the pick
         closes the menu in, Radix hands focus back to the trigger, and a disabled
         button cannot take it — so focus fell to <body> and a keyboard user whose
         write failed had to tab back from the top of the page.

       So the trigger stays focusable, says it is unavailable with
       `aria-disabled`, and every way of opening the menu — pointer, Enter, Space,
       ArrowDown — funnels through `onOpenChange`, which refuses while busy. */
    <DropdownMenu open={open && !busy && !noLabelsYet} onOpenChange={(next) => setOpen(next && !busy && !noLabelsYet)}>
      {/* Always wrapped, so a row changing hands keeps its trigger (and focus); only the
          content is conditional. The wrapper overwrites the trigger's `data-state`. */}
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        {setByHand ? <TooltipContent>Set manually — rules don't change this label</TooltipContent> : null}
      </Tooltip>

      {/* DARK surface, with its own Portal. 172px floor (12135-44223), 226px once the
          `Rule match` column shows (12135-44693); it grows if a long name would wrap it. */}
      {/* Both boxes hang below their button (chips 4px, add-label 5px): this puts the menu the usual 4px under the BOX. */}
      <DropdownMenuContent
        align="end"
        sideOffset={hasChips ? 8 : 9}
        className={cn('p-0', annotateMatches ? 'min-w-[226px]' : 'min-w-[172px]')}
      >
        {/* Shown, unlike the live frames (12483:51831): without it the menu never says that
            the reader is expected to choose. It also gives screen readers the conflict. */}
        {stage === 'needsDecision' ? (
          <DropdownMenuLabel className="flex flex-col px-3 pb-1 pt-2.5 font-normal">
            <span className="text-[13px] font-semibold text-white">Multiple rules match</span>
            <span className="text-xs text-neutral-300">Pick one</span>
          </DropdownMenuLabel>
        ) : null}

        <div className="p-1">
          {menuLabels.map((label) => {
            const isMatch = eligible.includes(label.labelId);
            const isApplied = label.labelId === applied;
            const name = `${label.name}${annotateMatches && isMatch ? ', rule match' : ''}${
              isApplied ? ', applied' : ''
            }`;

            return (
              <DropdownMenuItem
                key={label.labelId}
                aria-label={name}
                onSelect={() => onAssign(label.labelId)}
                className="gap-6 px-2 py-1.5"
              >
                {/* Never dimmed: every label is pickable, and dimmed reads as disabled. */}
                <span className="shrink-0">
                  <LabelChip categoryColor={category.color}>{label.name}</LabelChip>
                </span>

                {/* One right-hand column: the check slot is reserved on every row once
                    a label is applied, so each "Rule match" ends at the same edge. */}
                <span className="ml-auto flex items-center gap-3">
                  {annotateMatches && isMatch ? (
                    <span className="whitespace-nowrap text-[13px] text-neutral-100">Rule match</span>
                  ) : null}
                  {applied ? (
                    <span data-slot="check" className="flex size-4 shrink-0 items-center justify-center">
                      {isApplied ? (
                        <Check aria-hidden="true" className="size-4 text-green-400" strokeWidth={3} />
                      ) : null}
                    </span>
                  ) : null}
                </span>
              </DropdownMenuItem>
            );
          })}
        </div>

        {setByHand ? (
          <>
            <DropdownMenuSeparator className="my-0 bg-neutral-700" />
            <div className="p-1">
              <DropdownMenuItem onSelect={onReset} className="px-2 py-1.5">
                Remove manual assignment
              </DropdownMenuItem>
            </div>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
